import { Component, effect } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { NgFor, NgIf } from '@angular/common';
import { filter } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from './core/auth.service';
import { ApiService } from './core/api.service';
import { SocketService } from './core/socket.service';
import { ToastStackComponent } from './shared/toast-stack.component';
import { RESOURCE_CONFIG } from './pages/crud-page/crud.config';

interface LowStockMaterial {
  materialNumber: string;
  description: string;
  availableQuantity: number;
  threshold: number;
}

type NavSection =
  | 'Workspace'
  | 'Planning'
  | 'Reports'
  | 'Approvals'
  | 'Admin'
  | 'Logs';

interface NavItem {
  label: string;
  path: string;
  icon: string;
  section: NavSection;
  roles?: string[];
}

interface NavGroup {
  section: NavSection;
  items: NavItem[];
}

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NgFor,
    NgIf,
    MatIconModule,
    ToastStackComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  title = 'PMS';
  collapsed = false;
  mobileNavOpen = false;
  notificationsOpen = false;
  headerTitle = 'Dashboard';
  headerSubtitle = 'Live production command center';
  lowStockMaterials: LowStockMaterial[] = [];
  navGroups: NavGroup[] = [];
  private lastLowStockUserId = '';
  private readonly navSections: NavSection[] = [
    'Workspace',
    'Planning',
    'Approvals',
    'Reports',
    'Admin',
    'Logs',
  ];
  private readonly navItems: NavItem[] = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'dashboard',
      section: 'Workspace',
    },
    {
      label: 'Workers',
      path: '/workers',
      icon: 'groups',
      section: 'Workspace',
    },
    {
      label: 'Machines',
      path: '/machines',
      icon: 'precision_manufacturing',
      section: 'Workspace',
    },
    {
      label: 'Operations',
      path: '/operations',
      icon: 'schema',
      section: 'Workspace',
    },
    {
      label: 'Materials',
      path: '/materials',
      icon: 'inventory_2',
      section: 'Workspace',
    },
    {
      label: 'Calendar',
      path: '/holidays',
      icon: 'event_available',
      section: 'Planning',
    },
    {
      label: 'Assignments',
      path: '/assignments',
      icon: 'assignment',
      section: 'Planning',
    },
    {
      label: 'Production',
      path: '/production',
      icon: 'factory',
      section: 'Approvals',
    },
    {
      label: 'Approvals',
      path: '/approvals',
      icon: 'verified',
      section: 'Approvals',
    },
    {
      label: 'Attendance Report',
      path: '/attendance-report',
      icon: 'summarize',
      section: 'Reports',
    },
    {
      label: 'Approval Log',
      path: '/approval-log',
      icon: 'history',
      section: 'Logs',
    },
    {
      label: 'Material Log',
      path: '/material-usage',
      icon: 'warehouse',
      section: 'Logs',
    },
    {
      label: 'Biometric Logs',
      path: '/attendance',
      icon: 'fact_check',
      section: 'Logs',
    },
    {
      label: 'Admin Management',
      path: '/admin-management',
      icon: 'admin_panel_settings',
      section: 'Admin',
      roles: ['Superadmin'],
    },
  ];

  get visibleNavItems() {
    const role = this.auth.currentUser()?.role;
    return this.navItems.filter(
      (item) => !item.roles || (role && item.roles.includes(role)),
    );
  }

  constructor(
    public auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private api: ApiService,
    private socket: SocketService,
  ) {
    this.updateHeaderTitle();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateHeaderTitle();
      });
    effect(() => {
      this.buildNavGroups();
      const user = this.auth.currentUser();
      if (!user) {
        this.lastLowStockUserId = '';
        this.lowStockMaterials = [];
        return;
      }
      if (user.id !== this.lastLowStockUserId) {
        this.lastLowStockUserId = user.id;
        this.auth.startSessionHeartbeat();
        this.loadLowStockNotifications();
      }
    });
    this.socket
      .on<{ materials: LowStockMaterial[] }>('material:low-stock')
      .subscribe((payload) => {
        this.lowStockMaterials = payload.materials || [];
      });
    this.socket
      .on<{ userId?: string; email?: string }>('user:session-invalidated')
      .subscribe((payload) => {
        this.auth.handleSessionInvalidated(payload);
      });
    this.auth.startSessionHeartbeat();
  }

  trackNavGroup(_index: number, group: NavGroup) {
    return group.section;
  }

  trackNavItem(_index: number, item: NavItem) {
    return item.path;
  }

  /**
   * Logs out the current user and closes mobile navigation state.
   * @returns void.
   */
  logout() {
    this.mobileNavOpen = false;
    this.auth.logout();
    this.router.navigateByUrl('/login');
  }

  /**
   * Loads current low-stock materials for the shell notification area.
   * @returns void.
   */
  loadLowStockNotifications() {
    if (!this.auth.isLoggedIn()) return;
    this.api.get<LowStockMaterial[]>('/materials/low-stock').subscribe({
      next: (materials) => (this.lowStockMaterials = materials || []),
      error: () => (this.lowStockMaterials = []),
    });
  }

  private buildNavGroups() {
    const visibleItems = this.visibleNavItems;
    this.navGroups = this.navSections
      .map((section) => ({
        section,
        items: visibleItems.filter((item) => item.section === section),
      }))
      .filter((group) => group.items.length);
  }

  private updateHeaderTitle() {
    let active = this.route;
    while (active.firstChild) active = active.firstChild;

    const snapshot = active.snapshot;
    const resource = snapshot.data?.['resource'] as
      | keyof typeof RESOURCE_CONFIG
      | undefined;
    const path = snapshot.routeConfig?.path || '';

    if (resource && RESOURCE_CONFIG[resource]) {
      this.headerTitle = RESOURCE_CONFIG[resource].title;
      this.headerSubtitle = 'Enterprise data module';
      return;
    }

    const titles: Record<string, { title: string; subtitle: string }> = {
      dashboard: {
        title: 'Dashboard',
        subtitle: 'Live production command center',
      },
      holidays: { title: 'Holiday Calendar', subtitle: 'Scheduling calendar' },
      'attendance-report': {
        title: 'Production-Based Attendance',
        subtitle: 'Attendance report',
      },
      'admin-management': {
        title: 'Admin Management',
        subtitle: 'Superadmin module',
      },
    };
    const current = titles[path] || titles['dashboard'];
    this.headerTitle = current.title;
    this.headerSubtitle = current.subtitle;
  }
}
